import type { TranslatedString } from "@i18n";

/** Portal Chat web constants — storage keys, timings and list limits live here, never inline. */

export const SCOPE_STORAGE_KEY = "portalChatScope";
export const LAST_CONVERSATION_STORAGE_KEY = "portalChatLastConversation";
export const RECENT_EMOJIS_STORAGE_KEY = "portalChatRecentEmojis";

/** Typing signals: client floor between emits; how long a received signal stays visible. */
export const TYPING_EMIT_INTERVAL_MS = 2000;
export const TYPING_TTL_MS = 4000;
export const TYPING_SWEEP_INTERVAL_MS = 1500;
/** Manual reconnect delay after a non-terminal handshake denial (server says "retry"). */
export const SOCKET_RETRY_DELAY_MS = 10_000;
/** Mirrors the server's list-row preview truncation and unread-count cap (portalChat.constant.ts). */
export const PREVIEW_LENGTH = 140;
export const UNREAD_COUNT_CAP = 100;
/** Debounces: badge refresh while the panel is closed; directory search-as-you-type. */
export const BADGE_REFRESH_DEBOUNCE_MS = 3000;
export const DIRECTORY_SEARCH_DEBOUNCE_MS = 300;
/** People-picker page size (server caps `take` at 100; its own default page is 50). */
export const DIRECTORY_PAGE_SIZE = 50;
/** 60-second grouping window between consecutive messages of one sender (PRD §grouping). */
export const GROUPING_WINDOW_MS = 60 * 1000;
/** PRD: own messages are editable for 15 minutes after sending (mirrors the server constant). */
export const EDIT_WINDOW_MS = 15 * 60 * 1000;
/** PRD §4.4: a single message is capped at 4,000 characters (mirrored by the zod schema). */
export const MESSAGE_TEXT_MAX_LENGTH = 4000;
/** Mirrors the zod request caps so the UI never submits something the server will reject. */
export const GROUP_NAME_MAX_LENGTH = 120;
export const TOPIC_MAX_LENGTH = 300;
export const SECTION_NAME_MAX_LENGTH = 120;
export const SEARCH_MAX_LENGTH = 200;
export const ATTACHMENTS_MAX_COUNT = 20;
/** The open thread keeps at most this many messages in the DOM; older ones re-page on demand. */
export const THREAD_WINDOW_SIZE = 300;
/** Row heights for the virtualized lists (react-virtualized needs fixed heights). */
export const CONVERSATION_ROW_HEIGHT = 56;
export const GROUP_HEADER_ROW_HEIGHT = 32;
export const PICKER_ROW_HEIGHT = 48;
export const ATTACHMENT_ROW_HEIGHT = 40;

/**
 * Server error copy → i18n. The API answers with fixed English sentences (see
 * packages/server/portalChat/portalChat.constant.ts); each is registered as an en.json key and
 * translated through `t` here. Unknown messages fall back to the generic toast — no raw server
 * string is ever shown untranslated, and no `as TranslatedString` cast exists anywhere.
 */
const SERVER_ERROR_KEYS = [
  "Chat not found.",
  "This chat is read-only because its project is archived.",
  "You need a seat on this portal to use chat.",
  "Select at least one other member.",
  "Some selected users are not active members of this portal.",
  "A direct chat has exactly one other member.",
  "Could not start the chat. Please try again.",
  "A message needs text or an attachment.",
  "The message you replied to is no longer available.",
  "The message you forwarded is no longer available.",
  "This message can no longer be edited.",
  "Messages can only be edited within 15 minutes of sending.",
  "This message can no longer be deleted.",
  "This attachment can no longer be deleted.",
  "This message is no longer available.",
  "Only group chats can be renamed.",
  "Members can only be added to group chats.",
  "Members can only be removed from group chats.",
  "You can hide and mute a chat instead of leaving it.",
  "This user is not a member of the chat.",
  "Only the chat owner can remove members.",
  "Section not found.",
  "Project chats are ordered by project and can't be filed in sections.",
] as const;

type ServerErrorKey = (typeof SERVER_ERROR_KEYS)[number];

export function translateServerError(
  t: (key: ServerErrorKey) => TranslatedString,
  serverMessage: string | undefined,
  fallback: TranslatedString,
): TranslatedString {
  const known = SERVER_ERROR_KEYS.find((key) => key === serverMessage);
  return known ? t(known) : fallback;
}
